import { html, css } from "lit";
import { ifDefined } from "lit/directives/if-defined.js";
import { customElement, state } from "lit/decorators.js";
import type { TemplateResult } from "lit";

import type { VirtualCareContent } from "../../types/content";
import { WebKioskPageElement } from "../web-kiosk-page";

import "../../elements/web-kiosk-home-link";

declare global {
  interface HTMLElementTagNameMap {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    "web-kiosk-virtual-care": WebKioskVirtualCareElement;
  }
}

@customElement("web-kiosk-virtual-care")
export class WebKioskVirtualCareElement extends WebKioskPageElement {
  @state() content: VirtualCareContent | undefined = undefined;

  render(): TemplateResult {
    return html`<section id="virtual-care-page-container">
      <div id="left-panel">
        <div id="navigation">
          <web-kiosk-home-link
            link=${this.createRelativeLink("home")}
          ></web-kiosk-home-link>
        </div>
        <h1 id="left-header">${this.content?.leftHeader}</h1>
        <p id="left-paragraph">${this.content?.leftParagraph}</p>
      </div>

      <div id="middle-panel">
        <div>
          <h2 id="middle-header">${this.content?.middleHeader}</h2>
          <img
            src=${ifDefined(this.content?.phoneImg)}
            alt=${ifDefined(this.content?.phoneImgAlt)}
          />
        </div>
      </div>

      <div id="right-panel">
        <h3 id="right-header">${this.content?.rightHeader}</h3>
        <section class="phone">
          <h5 class="phone-h">${this.content?.newPhoneHeader}</h5>
          <p class="phone-p">${this.content?.newPhoneParagraph}</p>
          <h5 class="phone-h">${this.content?.oldPhoneHeader}</h5>
          <p class="phone-p" id="old-phone-p">
            ${this.content?.oldPhoneParagraph}
          </p>
        </section>
        <dd class="qr-code-container">
          <div class="speech-bubble">${this.content?.speechBubble}</div>
          <div class="speech-triangle"></div>
          <img
            id="qr-code"
            src=${ifDefined(this.content?.qrCode)}
            alt=${ifDefined(this.content?.qrCodeAlt)}
          />
        </dd>
      </div>
    </section>`;
  }

  static styles = css`
    :host {
      --web-kiosk-bg: #c1e8ff;
      --web-kiosk-secondary-bg: #323264;
      --web-kiosk-bg-grey: #f5f5f0;
      --web-kiosk-link-color: #284b9b;
      --web-kiosk-heading-text-color: #323264;
      --web-kiosk-paragraph-text-color: #414646;
      --web-kiosk-paragraph-secondary-text-color: #f9f9f6;
      --web-kiosk-panel-padding: 64px;
    }

    #virtual-care-page-container {
      display: flex;
      align-items: stretch;
      height: 100%;
      background-color: hotpink;
    }

    #navigation {
      display: flex;
      padding: 48px 0;
      color: var(--web-kiosk-link-color, hotpink);
      font-size: 32px;
    }

    #navigation a {
      width: 20%;
      display: flex;
      align-items: center;
      color: var(--web-kiosk-link-color, hotpink);
      text-decoration: none;
      font-weight: bold;
    }

    #navigation wh-icon {
      font-size: 50px;
      transform: rotate(90deg);
    }

    #left-panel {
      height: 100%;
      background-color: var(--web-kiosk-bg-grey, hotpink);
      width: 33%;
      box-sizing: border-box;
      padding: 0 52px;
    }

    #left-header {
      margin: 0 0 8px;
      color: var(--web-kiosk-heading-text-color, hotpink);
      font-size: 64px;
      font-weight: 700;
      line-height: 72px;
    }

    #left-paragraph {
      font-style: normal;
      font-weight: 400;
      font-size: 26px;
      line-height: 40px;
      color: var(--web-kiosk-paragraph-text-color, hotpink);
      margin-top: 100px;
    }

    #middle-panel {
      width: 42%;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      background: url("assets/mobile-app/bg/ribbon.svg") center right no-repeat;
      background-color: var(--web-kiosk-bg, hotpink);
      background-size: contain;
    }

    #middle-header {
      display: flex;
      justify-content: center;
      font-size: 48px;
      font-weight: 700;
      line-height: 62px;
      color: var(--web-kiosk-heading-text-color, hotpink);
    }

    #right-panel {
      width: 25%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: space-between;
      text-align: center;
      padding: 124px 64px 96px;
      background-color: var(--web-kiosk-secondary-bg, hotpink);
      background-size: contain;
    }

    #right-header {
      font-weight: 600;
      font-size: 32px;
      line-height: 32px;
      text-align: left;
      color: var(--web-kiosk-paragraph-secondary-text-color, hotpink);
    }

    .phone-h {
      font-style: normal;
      font-weight: 600;
      font-size: 22px;
      line-height: 32px;
      margin-bottom: 0;
      text-align: left;
      color: var(--web-kiosk-paragraph-secondary-text-color, hotpink);
    }

    .phone-p {
      font-style: normal;
      font-weight: 400;
      font-size: 20px;
      line-height: 28px;
      margin-top: 0;
      text-align: left;
      color: var(--web-kiosk-paragraph-secondary-text-color, hotpink);
    }

    #old-phone-p {
      margin-bottom: 48px;
    }

    .speech-bubble {
      background-color: var(--web-kiosk-bg, hotpink);
      width: 236px;
      height: 56px;
      border-radius: 42px;
      font-style: normal;
      font-weight: 700;
      font-size: 28px;
      color: var(--web-kiosk-heading-text-color, hotpink);
      line-height: 57px;
      text-align: center;
      vertical-align: center;
    }

    .speech-triangle {
      border-left: 20px solid transparent;
      border-right: 20px solid transparent;
      border-bottom: 6px solid transparent;
      border-top: 17px solid var(--web-kiosk-bg, hotpink);
    }

    .qr-code-container {
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    #qr-code {
      width: 232px;
      height: 232px;
    }
  `;
}
